import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Badge,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add,
  Remove,
  Settings,
  Warning,
  CheckCircle,
  Error,
  Inventory,
  TrendingDown,
  Refresh,
  CloudUpload
} from '@mui/icons-material';
import { stockAPI, alertesAPI, transfertsAPI } from '../../services/stockService';
import ImportCSVDialog from '../../components/stock/ImportCSVDialog';

const StockSecretairePage = () => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [alertes, setAlertes] = useState([]);
  const [transferts, setTransferts] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [alertesCount, setAlertesCount] = useState(0);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  // Dialogs
  const [openEntreeDialog, setOpenEntreeDialog] = useState(false);
  const [openSortieDialog, setOpenSortieDialog] = useState(false);
  const [openSeuilDialog, setOpenSeuilDialog] = useState(false);
  const [, setOpenTransfertDialog] = useState(false);
  
  // Form data
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [formData, setFormData] = useState({
    quantite: '',
    motif: '',
    numero_lot: '',
    date_expiration: '',
    seuil_alerte: ''
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (tabValue === 0) {
        // Onglet Stock
        const [stockRes, statsRes, alertesCountRes] = await Promise.all([
          stockAPI.getStockPrincipal(),
          stockAPI.getStatistiques(),
          alertesAPI.countNonLues()
        ]);
        setStock(stockRes.data.data || []);
        setStatistiques(statsRes.data.data || {});
        setAlertesCount(alertesCountRes.data.data?.count || 0);
      } else if (tabValue === 1) {
        // Onglet Alertes
        const alertesRes = await alertesAPI.getAlertes({ lu: false });
        setAlertes(alertesRes.data.data || []);
      } else if (tabValue === 2) {
        // Onglet Transferts
        const transfertsRes = await transfertsAPI.getEnAttente();
        setTransferts(transfertsRes.data.data || []);
      }
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEntreeDialog = (produit) => {
    setSelectedProduit(produit);
    setFormData({ quantite: '', motif: 'Réapprovisionnement', numero_lot: '', date_expiration: '' });
    setOpenEntreeDialog(true);
  };

  const handleOpenSortieDialog = (produit) => {
    setSelectedProduit(produit);
    setFormData({ quantite: '', motif: '', numero_lot: '', date_expiration: '' });
    setOpenSortieDialog(true);
  };

  const handleOpenSeuilDialog = (produit) => {
    setSelectedProduit(produit);
    setFormData({ seuil_alerte: produit.seuil_alerte });
    setOpenSeuilDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenEntreeDialog(false);
    setOpenSortieDialog(false);
    setOpenSeuilDialog(false);
    setOpenTransfertDialog(false);
    setSelectedProduit(null);
    setFormData({ quantite: '', motif: '', numero_lot: '', date_expiration: '', seuil_alerte: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEntreeStock = async (e) => {
    e.preventDefault();
    try {
      await stockAPI.ajouterStock({
        id_medicament: selectedProduit.id_medicament,
        quantite: parseInt(formData.quantite),
        motif: formData.motif,
        numero_lot: formData.numero_lot,
        date_expiration: formData.date_expiration || null
      });
      showSnackbar('Stock ajouté avec succès', 'success');
      handleCloseDialogs();
      loadData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors de l\'ajout', 'error');
    }
  };

  const handleSortieStock = async (e) => {
    e.preventDefault();
    try {
      await stockAPI.retirerStock({
        id_medicament: selectedProduit.id_medicament,
        quantite: parseInt(formData.quantite),
        motif: formData.motif
      });
      showSnackbar('Stock retiré avec succès', 'success');
      handleCloseDialogs();
      loadData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur lors du retrait', 'error');
    }
  };

  const handleUpdateSeuil = async (e) => {
    e.preventDefault();
    try {
      await stockAPI.updateSeuil(selectedProduit.id_medicament, {
        seuil_alerte: parseInt(formData.seuil_alerte)
      });
      showSnackbar('Seuil d\'alerte mis à jour', 'success');
      handleCloseDialogs();
      loadData();
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleMarquerAlerteLue = async (id) => {
    try {
      await alertesAPI.marquerLue(id);
      loadData();
    } catch (error) {
      showSnackbar('Erreur', 'error');
    }
  };

  const handleValiderTransfert = async (id, role) => {
    try {
      await transfertsAPI.validerTransfert(id, { role_validateur: role });
      showSnackbar('Transfert validé', 'success');
      loadData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Erreur', 'error');
    }
  };

  const getStatutChip = (statut) => {
    const configs = {
      'ok': { label: 'Stock OK', color: 'success', icon: <CheckCircle /> },
      'alerte': { label: 'Alerte', color: 'warning', icon: <Warning /> },
      'rupture': { label: 'Rupture', color: 'error', icon: <Error /> }
    };
    const config = configs[statut] || configs['ok'];
    return <Chip size="small" label={config.label} color={config.color} icon={config.icon} />;
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gestion du Stock Principal</Typography>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Actualiser
        </Button>
      </Box>

      <Button
        variant="outlined"
        color="primary"
        startIcon={<CloudUpload />}
        onClick={() => setOpenImportDialog(true)}
        sx={{ ml: 2 }}
      >
        Importer CSV
      </Button>

      {/* Statistiques */}
      {statistiques && tabValue === 0 && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Produits Total
                    </Typography>
                    <Typography variant="h4">
                      {statistiques.nb_produits || 0}
                    </Typography>
                  </Box>
                  <Inventory color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Alertes
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {statistiques.nb_alertes || 0}
                    </Typography>
                  </Box>
                  <Warning color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Ruptures
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {statistiques.nb_ruptures || 0}
                    </Typography>
                  </Box>
                  <Error color="error" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Valeur Totale
                    </Typography>
                    <Typography variant="h4">
                      {parseFloat(statistiques.valeur_totale || 0).toFixed(2)} €
                    </Typography>
                  </Box>
                  <TrendingDown color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Stock Principal" />
          <Tab 
            label={
              <Badge badgeContent={alertesCount} color="error">
                Alertes
              </Badge>
            } 
          />
          <Tab label="Transferts en attente" />
        </Tabs>
      </Paper>

      {/* Onglet Stock */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Médicament</strong></TableCell>
                <TableCell><strong>Unité</strong></TableCell>
                <TableCell align="center"><strong>Quantité</strong></TableCell>
                <TableCell align="center"><strong>Seuil</strong></TableCell>
                <TableCell align="center"><strong>Statut</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stock.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">Aucun produit en stock</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stock.map((produit) => (
                  <TableRow key={produit.id_medicament} hover>
                    <TableCell>{produit.nom_medicament}</TableCell>
                    <TableCell>{produit.unite}</TableCell>
                    <TableCell align="center">
                      <Typography 
                        variant="h6" 
                        color={produit.statut_stock === 'rupture' ? 'error' : 'inherit'}
                      >
                        {produit.quantite}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{produit.seuil_alerte}</TableCell>
                    <TableCell align="center">{getStatutChip(produit.statut_stock)}</TableCell>
                    <TableCell align="center">
                      <IconButton 
                        color="success" 
                        size="small"
                        onClick={() => handleOpenEntreeDialog(produit)}
                        title="Ajouter du stock"
                      >
                        <Add />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleOpenSortieDialog(produit)}
                        title="Retirer du stock"
                        disabled={produit.quantite === 0}
                      >
                        <Remove />
                      </IconButton>
                      <IconButton 
                        color="primary" 
                        size="small"
                        onClick={() => handleOpenSeuilDialog(produit)}
                        title="Modifier le seuil"
                      >
                        <Settings />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Onglet Alertes */}
      {tabValue === 1 && (
        <Paper>
          {alertes.length === 0 ? (
            <Box p={3} textAlign="center">
              <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune alerte en cours
              </Typography>
            </Box>
          ) : (
            alertes.map((alerte) => (
              <Alert 
                key={alerte.id_alerte}
                severity={alerte.niveau === 'danger' ? 'error' : 'warning'}
                onClose={() => handleMarquerAlerteLue(alerte.id_alerte)}
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">
                  <strong>{alerte.nom_medicament}</strong> - {alerte.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(alerte.date_creation).toLocaleString()}
                </Typography>
              </Alert>
            ))
          )}
        </Paper>
      )}

      {/* Onglet Transferts */}
      {tabValue === 2 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Médicament</strong></TableCell>
                <TableCell><strong>Destination</strong></TableCell>
                <TableCell align="center"><strong>Quantité</strong></TableCell>
                <TableCell><strong>Demandeur</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
                <TableCell align="center"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transferts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      Aucun transfert en attente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transferts.map((transfert) => (
                  <TableRow key={transfert.id_transfert} hover>
                    <TableCell>
                      {new Date(transfert.date_demande).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{transfert.nom_medicament}</TableCell>
                    <TableCell>{transfert.stock_destination}</TableCell>
                    <TableCell align="center">
                      <Chip label={transfert.quantite} color="primary" size="small" />
                    </TableCell>
                    <TableCell>{transfert.demandeur}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transfert.statut_libelle} 
                        color="warning" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      {transfert.statut === 'en_attente' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleValiderTransfert(transfert.id_transfert, 'source')}
                        >
                          Valider
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Entrée Stock */}
      <Dialog open={openEntreeDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ajouter du stock - {selectedProduit?.nom_medicament}
        </DialogTitle>
        <form onSubmit={handleEntreeStock}>
          <DialogContent>
            <TextField
              name="quantite"
              label="Quantité"
              type="number"
              fullWidth
              margin="normal"
              value={formData.quantite}
              onChange={handleChange}
              required
              inputProps={{ min: 1 }}
            />
            <TextField
              name="motif"
              label="Motif"
              fullWidth
              margin="normal"
              value={formData.motif}
              onChange={handleChange}
              required
            />
            <TextField
              name="numero_lot"
              label="Numéro de lot (optionnel)"
              fullWidth
              margin="normal"
              value={formData.numero_lot}
              onChange={handleChange}
            />
            <TextField
              name="date_expiration"
              label="Date d'expiration (optionnel)"
              type="date"
              fullWidth
              margin="normal"
              value={formData.date_expiration}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Annuler</Button>
            <Button type="submit" variant="contained" color="success">
              Ajouter
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Sortie Stock */}
      <Dialog open={openSortieDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Retirer du stock - {selectedProduit?.nom_medicament}
        </DialogTitle>
        <form onSubmit={handleSortieStock}>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Stock disponible : <strong>{selectedProduit?.quantite}</strong>
            </Alert>
            <TextField
              name="quantite"
              label="Quantité à retirer"
              type="number"
              fullWidth
              margin="normal"
              value={formData.quantite}
              onChange={handleChange}
              required
              inputProps={{ 
                min: 1, 
                max: selectedProduit?.quantite || 0 
              }}
            />
            <TextField
              name="motif"
              label="Motif"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={formData.motif}
              onChange={handleChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Annuler</Button>
            <Button type="submit" variant="contained" color="error">
              Retirer
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Modifier Seuil */}
      <Dialog open={openSeuilDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>
          Modifier le seuil d'alerte - {selectedProduit?.nom_medicament}
        </DialogTitle>
        <form onSubmit={handleUpdateSeuil}>
          <DialogContent>
            <TextField
              name="seuil_alerte"
              label="Nouveau seuil d'alerte"
              type="number"
              fullWidth
              margin="normal"
              value={formData.seuil_alerte}
              onChange={handleChange}
              required
              inputProps={{ min: 0 }}
              helperText="Vous serez alerté quand le stock passe en-dessous de ce seuil"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialogs}>Annuler</Button>
            <Button type="submit" variant="contained">
              Modifier
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog Import CSV */}
      <ImportCSVDialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        onSuccess={loadData}
        apiEndpoint={`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/stock/import/principal`}
        title="Import CSV - Stock Principal"
      />
    </Container>
  );
};

export default StockSecretairePage;
